import React, { useEffect, useState } from 'react';

import { history } from 'helpers';
import { useQueryString } from 'hooks';

import { removeToken } from 'lane-shared/helpers/integrations/BuildingEnginesPrism/token';
import { ContentType } from 'lane-shared/types/content/Content';

import { UrlSearchParamsWithPages } from 'components/lds/table/types';

import TabStrip, { TabItem } from '../../../general/TabStrip';
import Header from './Header';
import VisitorsTable from './VisitorsTable';
import Loading from '../../../general/Loading';

import styles from './VisitorsContainer.scss';
import BuildingSelectionDropdown from 'components/integrations/BuildingEnginesPrism/components/BuildingSelectionDropdown';
import {useBEPrismBuildingsContext} from 'lane-shared/contexts/BuildingEnginesPrism/useBEPrismBuildingsContext';
import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';
import { AccessDeniedCard } from 'components/integrations/BuildingEnginesPrism/components/AccessDeniedCard';

interface TabTypes {
  upcoming: TabItem;
  past: TabItem;
}

export const tabs: TabTypes = {
  upcoming: {
    label: 'Upcoming',
    value: 'upcoming',
  },
  past: {
    label: 'Past',
    value: 'past',
  },
};

export type VisitorQueryParams = UrlSearchParamsWithPages & {
  selectedTab: 'upcoming' | 'past';
};

type Props = {
  content: ContentType;
};

export default function VisitorsContainer({ content }: Props) {
  const [selectedTab, setSelectedTab] = useState<TabItem>(tabs.upcoming);
  const [
    urlSearchParams,
    setUrlSearchParams,
  ] = useQueryString<VisitorQueryParams>({
    selectedTab: 'upcoming',
    page: 0,
    perPage: 10,
    total: 0,
    sortKey: '',
    dir: '',
  });

  const isBEFFEnabled = useFlag(FeatureFlag.EnableSSOAndMultiBuildingForBE, false);
  const { buildingId: selectedBuildingId, setBuildingId, buildings, loadingBuildings} = useBEPrismBuildingsContext();
  const buildingId = isBEFFEnabled ? selectedBuildingId : content?.integration?.settings?.buildingId;

  const handleBuildingChange = (newBuildingId: string) => {
    setBuildingId(newBuildingId);
    const currentState = history.location.state || {};

    history.replace({
      ...history.location,
      state: { ...currentState, buildingId: newBuildingId },
    });
  };

  useEffect(() => {
    const locationBuildingId = (
      history.location?.state as { buildingId?: string }
    )?.buildingId;

    if (isBEFFEnabled && !loadingBuildings && locationBuildingId) {
      setBuildingId(locationBuildingId);
    }
  }, [isBEFFEnabled, loadingBuildings, setBuildingId]);

  useEffect(() => {
    setSelectedTab(tabs[urlSearchParams.selectedTab]);
  }, []);

  const setSelectedTabAndUrlParam = (tab: TabItem): void => {
    setUrlSearchParams({ page: 0, selectedTab: tab.value });
    setSelectedTab(tab);
  };

  function onLogout() {
    removeToken();
    history.goBack();
  }

  if (isBEFFEnabled && loadingBuildings) {
    return (
      <div className={styles.visitorsContainer}>
        <Header content={content} onLogout={onLogout} />
        <Loading />
      </div>
    );
  }

  if(isBEFFEnabled && (!buildings || buildings.length === 0)){
    return(
      <div className={styles.visitorsContainer}>
         <Header content={content} onLogout={onLogout} />
        <AccessDeniedCard />
      </div>
    );
  }

  return (
    <div className={styles.visitorsContainer}>
        <Header content={content} onLogout={onLogout} />
        {isBEFFEnabled && buildings && buildings.length > 1 && (
          <div className={styles.buildingSelectionDropdownContainer}>
            <BuildingSelectionDropdown
              buildings={buildings}
              buildingId={buildingId}
              setBuildingId={handleBuildingChange}
              showLabel
              labelClassName={styles.label}
            />
          </div>
        )}
        <TabStrip
          tabs={Object.values(tabs)}
          selected={selectedTab}
          onSelectTab={tab => setSelectedTabAndUrlParam(tab)}
          className={styles.visitorTabs}
        />
        <section>
          <VisitorsTable
            paginationParams={urlSearchParams}
            setUrlSearchParams={setUrlSearchParams}
            content={content}
          />
        </section>
      </div>
  );
}
